import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChangeDetectorRef } from '@angular/core';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../../../services/auth/auth.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-definir-senha',
  templateUrl: './definir-senha.html',
  styleUrls: ['./definir-senha.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CommonModule,
    MatError,
    MatButtonModule,
  ],
})
export class DefinirSenha implements OnInit {
  titulo = 'Defina sua nova senha';
  isPrimeiroAcesso = false;

  senhaFormGroup = new FormGroup({
    senha: new FormControl(''),
    confirmarSenha: new FormControl(''),
  });

  // Controle dos requisitos da senha
  passwordRequirements = {
    minLength: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
  };

  confirmarSenhaRequirements = {
    equals: true,
    touched: false,
  };

  // Estado de validação
  validationState = {
    senhaValid: false,
    confirmacaoValid: false,
  };

  ngOnInit(): void {
    this.isPrimeiroAcesso = this.router.url.includes('primeiro-acesso');
    this.titulo = this.isPrimeiroAcesso
      ? 'Definir senha'
      : 'Defina sua nova senha';
  }

  constructor(
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly snackbarService: SnackbarService
  ) {}

  onPasswordInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;

    // Atualiza os requisitos da senha
    this.passwordRequirements = {
      minLength: value.length >= 8,
      upperCase: /[A-Z]/.test(value),
      lowerCase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    };

    this.validationState.senhaValid = this.isPasswordValid();
    this.updateConfirmacaoValidation();
  }

  onConfirmacaoInput(event?: Event) {
    const confirmacao = event
      ? (event.target as HTMLInputElement).value
      : this.senhaFormGroup.get('confirmarSenha')?.value;
    const senha = this.senhaFormGroup.get('senha')?.value;

    this.confirmarSenhaRequirements = {
      equals: senha === confirmacao,
      touched: true, // Marca como touched ao digitar
    };

    this.validationState.confirmacaoValid =
      this.confirmarSenhaRequirements.equals && confirmacao !== '';
    this.changeDetectorRef.detectChanges();
  }

  private updateConfirmacaoValidation() {
    const senha = this.senhaFormGroup.get('senha')?.value;
    const confirmacao = this.senhaFormGroup.get('confirmarSenha')?.value;
    this.validationState.confirmacaoValid =
      senha === confirmacao && confirmacao !== '';
    this.changeDetectorRef.detectChanges();
  }

  isPasswordValid(): boolean {
    const { minLength, upperCase, lowerCase, number, specialChar } =
      this.passwordRequirements;
    return minLength && upperCase && lowerCase && (number || specialChar);
  }

  isFormValid(): boolean {
    return (
      this.validationState.senhaValid && this.validationState.confirmacaoValid
    );
  }

  async enviarNovaSenha() {
    if (!this.isFormValid()) {
      this.snackbarService.error('Por favor, preencha todos os campos corretamente');
      return;
    }

    const senha = this.senhaFormGroup.get('senha')?.value ?? '';

    try {
      if (this.isPrimeiroAcesso) {
        const idUsuario = Number(sessionStorage.getItem('primeiroAcessoId'));
        const email = sessionStorage.getItem('primeiroAcessoEmail') ?? '';

        await this.authService.definirSenhaPrimeiroAcesso(
          idUsuario,
          email,
          senha
        );
        this.snackbarService.success('Senha definida com sucesso!');
        this.router.navigate(['/auth/primeiro-acesso/senha-definida-sucesso']);
      } else {
        const token = sessionStorage.getItem('token') ?? '';
        await this.authService.redefinirSenha(senha, token);
        this.snackbarService.success('Senha redefinida com sucesso!');
        this.router.navigate(['/auth/login']);
      }
    } catch (err: any) {
      this.snackbarService.error(
        err.response?.message || err.message || 'Erro ao salvar senha'
      );
    }
  }


  voltarParaLogin() {
    this.router.navigate(['/auth/login']);
  }
}
